import React, { useContext, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { GlobalContext, UserContext } from '../../../App'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import SubscriptionAgreementDialog, {
  useSubscriptionAgreementStatus,
} from '../../../legal/SubscriptionAgreementDialog'

// What the reminder says, by why the agreement is waiting (`reason` from
// GET /billing/subscription-agreement). `who` is the capacity in which the
// person reading it is being asked.
const reminderCopy = (reason, who) => {
  if (reason === 'planChanged') {
    return {
      title: 'Updated Plan Summary to review',
      message: `Your plan has changed and AmbuNet asks you, ${who}, to review and accept the updated Plan Summary under its Business Subscription Agreement — nothing about your access changes in the meantime.`,
    }
  }
  // Existing customers first seeing the agreement ('neverAccepted') and those
  // asked to accept a newer version ('newVersion') see the same message: from
  // their side the agreement has been updated.
  return {
    title: 'Business Subscription Agreement has been updated',
    message: `AmbuNet has updated its Business Subscription Agreement and asks you, ${who}, to review and accept it with your Plan Summary — nothing about your access changes in the meantime.`,
  }
}

const ReminderBanner = () => {
  const { tenantInfo } = useContext(GlobalContext)
  const { user } = useContext(UserContext)
  const [reviewOpen, setReviewOpen] = useState(false)

  const isOwner = user?.security?.isTenantOwner === true
  const mayReview =
    (isOwner || user?.security?.isSiteAdmin === true) && !tenantInfo?.isDemo

  const { agreementStatus, loading, refresh, applyStatus } =
    useSubscriptionAgreementStatus(mayReview)

  if (!agreementStatus) return null

  const showReminder = agreementStatus.showReminder === true
  const { title, message } = reminderCopy(
    agreementStatus.reason,
    isOwner ? "as your organisation's owner" : 'as a site administrator',
  )

  return (
    <>
      {showReminder && (
        <Box
          role="status"
          sx={{
            flexShrink: 0,
            mx: { xs: 1, md: 2 },
            mt: 1.5,
            mb: 1,
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.25, md: 1.5 },
            borderRadius: 2,
            border: '1px solid rgba(230, 126, 34, 0.28)',
            background:
              'linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 255, 255, 0.96) 48%, rgba(232, 244, 248, 0.94) 100%)',
            boxShadow:
              '0 18px 45px rgba(36, 47, 64, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
            color: '#243040',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              width: 5,
              background: 'linear-gradient(180deg, #F59E0B 0%, #0E7490 100%)',
            },
            // A short window (a phone on its side, a zoomed-in laptop) has
            // no height to give away: keep just the title and the button so
            // the page underneath stays usable.
            '@media (max-height: 480px)': {
              mt: 1,
              mb: 0.5,
              py: 0.75,
              '& .agreement-reminder-message, & .agreement-reminder-icon': {
                display: 'none',
              },
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1.25, sm: 2 }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            sx={{
              pl: { xs: 1, md: 1.25 },
              '@media (max-height: 480px)': {
                flexDirection: 'row',
                alignItems: 'center',
              },
            }}
          >
            <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
              <Box
                className="agreement-reminder-icon"
                sx={{
                  width: 42,
                  height: 42,
                  flex: '0 0 auto',
                  borderRadius: 1.5,
                  display: { xs: 'none', sm: 'grid' },
                  placeItems: 'center',
                  color: '#92400E',
                  background:
                    'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(14, 116, 144, 0.12))',
                  border: '1px solid rgba(245, 158, 11, 0.22)',
                }}
              >
                <DescriptionRoundedIcon fontSize="small" />
              </Box>
              <Box minWidth={0}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    color: '#111827',
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  className="agreement-reminder-message"
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: '#4B5563',
                    lineHeight: 1.45,
                  }}
                >
                  {message}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: 'flex-end', sm: 'center' }}
              flexShrink={0}
            >
              <Button
                onClick={() => setReviewOpen(true)}
                size="small"
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  minHeight: 36,
                  borderRadius: 1.5,
                  px: 1.75,
                  backgroundColor: '#111827',
                  color: '#fff',
                  boxShadow: '0 10px 24px rgba(17, 24, 39, 0.18)',
                  textTransform: 'none',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#0E7490',
                    boxShadow: '0 12px 28px rgba(14, 116, 144, 0.24)',
                  },
                }}
              >
                Review agreement
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}
      {/* A sibling of the banner, never a child: a MUI portal still bubbles
          React events to its React ancestors. It stays mounted once the
          banner has gone so that it can finish closing. */}
      <SubscriptionAgreementDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        agreementStatus={agreementStatus}
        loading={loading}
        onRefresh={refresh}
        onStatusChange={applyStatus}
      />
    </>
  )
}

/**
 * Reminder that the AmbuNet Business Subscription Agreement (and the
 * organisation's Plan Summary) is waiting to be reviewed and accepted.
 *
 * It is a reminder and nothing more: a banner in the normal page flow with
 * one button. It never overlays, redirects or holds up a page, and the review
 * dialog only opens when that button is clicked. There is deliberately no
 * dismiss control — it stays on every page until the agreement is accepted.
 *
 * Mounted in MainFrame's normal site mode only, so it cannot appear inside
 * the ePCR, control room, treatment centre, PTS or portal screens.
 *
 * The status is only requested for the tenant owner or a site administrator
 * (never on a demo tenant), and the server decides who is actually reminded:
 * the owner, or site administrators when the organisation has no owner.
 * Anything other than a clear "show it" renders nothing.
 *
 * The boundary is the last line of defence for the same rule: if the banner
 * or its dialog ever fails to render, they disappear and the page underneath
 * carries on, rather than the failure reaching the app-wide error page.
 */
const AgreementReminder = () => (
  <ErrorBoundary fallbackRender={() => null} onError={console.error}>
    <ReminderBanner />
  </ErrorBoundary>
)

export default AgreementReminder
